import type { BeneficiaryContactPayload } from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Actions/beneficiaryContactsFetched';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `beneficiary`, `createdAt` and `updatedAt` are never sent by the API on this route (§7 of the plan)
 * and are deliberately not declared here.
 */
class BeneficiaryContactView {
	readonly id: number;
	readonly firstName: string;
	readonly lastName: string;
	readonly role: LabeledValueView | null;
	readonly isPersonOfTrust: boolean;
	readonly roleDetails: string | null;
	readonly relationship: LabeledValueView;
	readonly phoneNumber: string | null;
	readonly email: string | null;
	readonly notes: string | null;
	readonly facilityId: number;

	constructor(payload: BeneficiaryContactPayload) {
		this.id = payload.id;
		this.firstName = payload.firstName;
		this.lastName = payload.lastName;
		this.role = payload.role !== null ? new LabeledValueView(payload.role) : null;
		this.isPersonOfTrust = payload.isPersonOfTrust;
		this.roleDetails = payload.roleDetails;
		this.relationship = new LabeledValueView(payload.relationship);
		this.phoneNumber = payload.phoneNumber;
		this.email = payload.email;
		this.notes = payload.notes;
		this.facilityId = payload.facility.id;

		Object.freeze(this);
	}

	/**
	 * Surname first, French administrative order — same convention as `BeneficiaryListItemView.fullName`.
	 * It also doubles as the name a user retypes in the delete confirmation dialog.
	 */
	get fullName(): string {
		return `${this.lastName} ${this.firstName}`;
	}

	/**
	 * What qualifies this contact, in one line: the professional role when there is one, the family or
	 * social tie otherwise — the two answer the same question and never both apply — then the person of
	 * trust mention, which stacks onto either. Returns `null` for nothing to say, so a caller renders no
	 * empty second line.
	 */
	get qualification(): string | null {
		const parts = [this.role !== null ? this.role.label : this.relationship.label];

		if (this.isPersonOfTrust) {
			parts.push('personne de confiance');
		}

		return parts.length > 0 ? parts.join(' · ') : null;
	}
}

export default BeneficiaryContactView;
